/**
 * Mode clair / sombre de l'app, pour TOUS les rôles.
 *
 * - Sombre = `.theme-ocean` sur <html> (tokens inversés, cf. globals.css).
 * - Clair  = `.theme-light` (palette claire du preset, refroidie).
 *
 * Préférence choisie dans « Mon profil » : `system` (défaut, suit le téléphone),
 * `light` ou `dark`. Mémorisée SUR L'APPAREIL (localStorage) → appliquée dès le
 * démarrage, y compris sur l'écran de connexion, sans requête. Un script
 * inline de index.html pose la bonne classe AVANT le 1er rendu (pas de flash) ;
 * `initAppTheme()` (main.tsx) prend ensuite le relais et suit le système.
 */
import { useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export const DARK_THEME_CLASS = 'theme-ocean';
export const LIGHT_THEME_CLASS = 'theme-light';
/** Clé localStorage — doit rester identique au script inline de index.html. */
export const THEME_STORAGE_KEY = 'jr_theme';

const DARK_CHROME = '#061536';
const LIGHT_CHROME = '#eaf3ff';

export type ThemePreference = 'system' | 'light' | 'dark';
export type AppTheme = 'light' | 'dark';

export const THEME_PREFERENCES: readonly { value: ThemePreference; label: string }[] = [
  { value: 'system', label: 'Automatique' },
  { value: 'light', label: 'Clair' },
  { value: 'dark', label: 'Sombre' },
];

export function parseThemePreference(raw: string | null | undefined): ThemePreference {
  return raw === 'light' || raw === 'dark' ? raw : 'system';
}

/** Règle pure : préférence + réglage système → thème effectif. */
export function resolveTheme(pref: ThemePreference, systemPrefersDark: boolean): AppTheme {
  if (pref === 'system') return systemPrefersDark ? 'dark' : 'light';
  return pref;
}

export function readThemePreference(): ThemePreference {
  try {
    return parseThemePreference(localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return 'system';
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

/** Applique un thème sur <html> (classes + couleur de chrome + barre de statut native). */
export function applyAppTheme(theme: AppTheme, root: HTMLElement = document.documentElement): void {
  root.classList.toggle(DARK_THEME_CLASS, theme === 'dark');
  root.classList.toggle(LIGHT_THEME_CLASS, theme === 'light');

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = theme === 'light' ? LIGHT_CHROME : DARK_CHROME;

  if (Capacitor.isNativePlatform()) {
    // Style.Light = texte SOMBRE (fond clair) ; Style.Dark = texte clair.
    void StatusBar.setStyle({ style: theme === 'light' ? Style.Light : Style.Dark }).catch(
      () => {},
    );
  }
}

// ── Store (préférence partagée entre le contrôle du profil et <html>) ────────

let currentPref: ThemePreference | null = null;
const listeners = new Set<() => void>();

function getPref(): ThemePreference {
  if (currentPref === null) currentPref = readThemePreference();
  return currentPref;
}

function applyCurrent(): void {
  applyAppTheme(resolveTheme(getPref(), systemPrefersDark()));
}

export function setThemePreference(pref: ThemePreference): void {
  currentPref = pref;
  try {
    if (pref === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, pref);
  } catch {
    /* stockage indisponible (navigation privée) : vaut pour la session */
  }
  applyCurrent();
  listeners.forEach((l) => l());
}

let initialized = false;

/** À appeler une fois au démarrage : applique la préférence et suit le système. */
export function initAppTheme(): void {
  applyCurrent();
  if (initialized) return;
  initialized = true;
  try {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
      if (getPref() === 'system') applyCurrent();
    });
  } catch {
    /* matchMedia indisponible */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Préférence courante + setter, pour le sélecteur du profil. */
export function useThemePreference(): [ThemePreference, (pref: ThemePreference) => void] {
  const pref = useSyncExternalStore(subscribe, getPref, () => 'system' as const);
  return [pref, setThemePreference];
}

/** Réinitialise l'état du module (tests uniquement). */
export function __resetThemeStoreForTests(): void {
  currentPref = null;
  listeners.clear();
}
